import React, { useCallback, useEffect, useState } from 'react';
import { FiEye, FiEyeOff, FiShield, FiUsers, FiX } from 'react-icons/fi';
import {
  clientPortalAdminService,
  type ClientPortalMemberRow,
  type ClientPortalSettingsPatch,
} from '../../services/clientPortalAdminService';
import type {
  ClientPortalSettings,
  GuardContactVisibility,
  ReportReleaseMode,
} from '../../services/clientPortalService';
import { useI18n } from '../../i18n';
import './ClientPortalSettingsModal.css';

interface Props {
  clientContractId: string;
  clientName: string;
  onClose: () => void;
}

type ToggleKey = keyof Pick<
  ClientPortalSettings,
  | 'live_tracking_enabled'
  | 'active_guards_enabled'
  | 'patrol_progress_enabled'
  | 'incident_reports_enabled'
  | 'daily_reports_enabled'
  | 'patrol_records_enabled'
  | 'visitor_logs_enabled'
  | 'post_orders_enabled'
  | 'schedule_enabled'
  | 'service_requests_enabled'
  | 'exports_enabled'
  | 'sub_accounts_enabled'
>;

interface ToggleGroup {
  title: string;
  summary: string;
  items: Array<{ key: ToggleKey; label: string; hint: string }>;
}

const GROUPS: ToggleGroup[] = [
  {
    title: 'Operations & real-time monitoring',
    summary: 'What the client can see while officers are on shift.',
    items: [
      {
        key: 'live_tracking_enabled',
        label: 'Live GPS map',
        hint: 'Real-time officer positions. Off by default — enable only where the client has agreed.',
      },
      { key: 'active_guards_enabled', label: 'On-duty officer list', hint: 'Names and shift times of officers currently clocked in.' },
      { key: 'patrol_progress_enabled', label: 'Live patrol progress', hint: 'Checkpoint scans as a tour is walked.' },
    ],
  },
  {
    title: 'Reports & accountability',
    summary: 'Which feeds reach the portal, and how soon.',
    items: [
      { key: 'incident_reports_enabled', label: 'Incident reports', hint: 'Severity, description, time and location stamps, media.' },
      { key: 'daily_reports_enabled', label: 'Daily activity reports', hint: 'Shift summaries and routine field observations.' },
      { key: 'patrol_records_enabled', label: 'Guard tour records', hint: 'Completed, delayed and missed patrol sweeps.' },
      { key: 'visitor_logs_enabled', label: 'Visitor & vehicle logs', hint: 'Access control records captured at entry points.' },
      { key: 'exports_enabled', label: 'PDF and Word exports', hint: 'Let the client download audit-ready copies.' },
    ],
  },
  {
    title: 'Site context & scheduling',
    summary: 'Directives, coverage and requests.',
    items: [
      { key: 'post_orders_enabled', label: 'Post orders', hint: 'Published site rules, duties and emergency procedures.' },
      { key: 'schedule_enabled', label: 'Coverage calendar', hint: 'Published duties confirming booked cover.' },
      { key: 'service_requests_enabled', label: 'On-demand requests', hint: 'Let the client request extra hours or emergency dispatch.' },
      { key: 'sub_accounts_enabled', label: 'Sub-accounts', hint: 'Let the primary contact create limited logins for colleagues.' },
    ],
  },
];

const CONTACT_VISIBILITY: Array<{ value: GuardContactVisibility; label: string; hint: string }> = [
  { value: 'full', label: 'Name and phone', hint: 'The client can call the officer directly.' },
  { value: 'name_only', label: 'Name only', hint: 'Identifies the officer without exposing contact details.' },
  { value: 'hidden', label: 'Anonymised', hint: 'Shows that a post is covered without naming who.' },
];

export default function ClientPortalSettingsModal({ clientContractId, clientName, onClose }: Props) {
  const { t } = useI18n();
  const [settings, setSettings] = useState<ClientPortalSettings | null>(null);
  const [members, setMembers] = useState<ClientPortalMemberRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const [settingsRow, memberRows] = await Promise.all([
        clientPortalAdminService.fetchSettings(clientContractId),
        clientPortalAdminService.fetchMembers(clientContractId),
      ]);
      setSettings(settingsRow);
      setMembers(memberRows);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [clientContractId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(''), 2500);
    return () => window.clearTimeout(timer);
  }, [toast]);

  // Applied optimistically so a row of switches stays responsive; a failure
  // resyncs from the server rather than leaving the UI lying.
  const patch = async (next: ClientPortalSettingsPatch) => {
    if (!settings) return;
    const previous = settings;
    setSettings({ ...settings, ...next } as ClientPortalSettings);
    setIsBusy(true);
    try {
      const saved = await clientPortalAdminService.updateSettings(clientContractId, next);
      setSettings(saved);
      setToast(t('Saved'));
    } catch (err) {
      setSettings(previous);
      setError((err as Error).message);
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div className="modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="modal cps-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t('Portal visibility for {name}', { name: clientName })}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="cps-modal__head">
          <div>
            <div className="cps-modal__eyebrow">{t('Client portal control')}</div>
            <h2>{clientName}</h2>
            <p>{t('Decide exactly what this client can see. Changes apply immediately.')}</p>
          </div>
          <div className="cps-modal__head-actions">
            {toast ? <span className="cps-saved">{toast}</span> : null}
            <button type="button" className="cps-icon-btn" aria-label={t('Close')} onClick={onClose}>
              <FiX aria-hidden="true" />
            </button>
          </div>
        </header>

        {error ? <div className="cps-error">{error}</div> : null}

        {isLoading || !settings ? (
          <div className="cps-loading"><div className="spinner" /></div>
        ) : (
          <div className="cps-modal__body">
            {GROUPS.map((group) => (
              <section key={group.title} className="cps-group">
                <header>
                  <h3>{t(group.title)}</h3>
                  <p>{t(group.summary)}</p>
                </header>
                <div className="cps-toggles">
                  {group.items.map((item) => (
                    <label key={item.key} className="cps-toggle">
                      <input
                        type="checkbox"
                        checked={Boolean(settings[item.key])}
                        disabled={isBusy}
                        onChange={(event) => void patch({ [item.key]: event.target.checked } as ClientPortalSettingsPatch)}
                      />
                      <span className="cps-toggle__switch" aria-hidden="true" />
                      <span className="cps-toggle__text">
                        <strong>{t(item.label)}</strong>
                        <small>{t(item.hint)}</small>
                      </span>
                    </label>
                  ))}
                </div>
              </section>
            ))}

            <section className="cps-group">
              <header>
                <h3>{t('Report approval rules')}</h3>
                <p>{t('Choose whether submissions publish on the spot or wait for supervisor review.')}</p>
              </header>

              <div className="cps-choices">
                <div className="cps-choice">
                  <span className="cps-choice__label">{t('Incident reports')}</span>
                  <div className="cps-segment" role="radiogroup" aria-label={t('Incident report release')}>
                    {(['immediate', 'on_approval'] as ReportReleaseMode[]).map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        role="radio"
                        aria-checked={settings.incident_release_mode === mode}
                        className={`cps-segment__btn${settings.incident_release_mode === mode ? ' cps-segment__btn--active' : ''}`}
                        disabled={isBusy}
                        onClick={() => void patch({ incident_release_mode: mode })}
                      >
                        {mode === 'immediate' ? t('On submission') : t('After review')}
                      </button>
                    ))}
                  </div>
                  <small>
                    {settings.incident_release_mode === 'immediate'
                      ? t('Incidents appear in the portal the moment a guard submits them.')
                      : t('Incidents stay hidden until a supervisor releases them from the report screen.')}
                  </small>
                </div>

                <div className="cps-choice">
                  <span className="cps-choice__label">{t('Daily, patrol and visitor reports')}</span>
                  <div className="cps-segment" role="radiogroup" aria-label={t('Routine report release')}>
                    {(['immediate', 'on_approval'] as ReportReleaseMode[]).map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        role="radio"
                        aria-checked={settings.daily_report_release_mode === mode}
                        className={`cps-segment__btn${settings.daily_report_release_mode === mode ? ' cps-segment__btn--active' : ''}`}
                        disabled={isBusy}
                        onClick={() => void patch({ daily_report_release_mode: mode })}
                      >
                        {mode === 'immediate' ? t('On submission') : t('After review')}
                      </button>
                    ))}
                  </div>
                  <small>{t('Applies to routine paperwork submitted from the field app.')}</small>
                </div>
              </div>
            </section>

            <section className="cps-group">
              <header>
                <h3>{t('Officer identity')}</h3>
                <p>{t('How much of an on-duty officer the client sees.')}</p>
              </header>
              <div className="cps-toggles">
                {CONTACT_VISIBILITY.map((option) => (
                  <label key={option.value} className="cps-toggle cps-toggle--radio">
                    <input
                      type="radio"
                      name="guard-contact-visibility"
                      checked={settings.guard_contact_visibility === option.value}
                      disabled={isBusy}
                      onChange={() => void patch({ guard_contact_visibility: option.value })}
                    />
                    <span className="cps-toggle__text">
                      <strong>
                        {option.value === 'hidden' ? <FiEyeOff aria-hidden="true" /> : <FiEye aria-hidden="true" />}
                        {t(option.label)}
                      </strong>
                      <small>{t(option.hint)}</small>
                    </span>
                  </label>
                ))}
              </div>
            </section>

            <section className="cps-group">
              <header>
                <h3>{t('History window')}</h3>
                <p>{t('How far back the portal may read. Leave empty for the full contract history.')}</p>
              </header>
              <label className="cps-days">
                <input
                  type="number"
                  min={1}
                  max={3650}
                  className="form__input"
                  value={settings.report_history_days ?? ''}
                  disabled={isBusy}
                  placeholder={t('Unlimited')}
                  onChange={(event) => {
                    const raw = event.target.value;
                    setSettings({
                      ...settings,
                      report_history_days: raw === '' ? null : Number(raw),
                    });
                  }}
                  onBlur={(event) => {
                    const raw = event.target.value;
                    void patch({ report_history_days: raw === '' ? null : Number(raw) });
                  }}
                />
                <span>{t('days')}</span>
              </label>
            </section>

            <section className="cps-group">
              <header>
                <h3><FiUsers aria-hidden="true" /> {t('Portal accounts')}</h3>
                <p>{t('Who at this client can sign in, and what the primary contact has granted them.')}</p>
              </header>

              {members.length === 0 ? (
                <div className="cps-empty">{t('No portal accounts have been activated yet.')}</div>
              ) : (
                <ul className="cps-members">
                  {members.map((member) => (
                    <li key={member.id}>
                      <div>
                        <strong>{member.full_name ?? member.email}</strong>
                        <span>{member.email}{member.job_title ? ` · ${member.job_title}` : ''}</span>
                      </div>
                      <div className="cps-members__meta">
                        <span className={`cps-pill${member.role === 'primary' ? ' cps-pill--primary' : ''}`}>
                          {member.role === 'primary' ? t('Primary contact') : t('Sub-account')}
                        </span>
                        <span className={`cps-pill${member.status === 'disabled' ? ' cps-pill--off' : ''}`}>
                          {member.status === 'active' ? t('Active') : t('Disabled')}
                        </span>
                        {member.role === 'member' ? (
                          <span className="cps-members__scopes">
                            {member.permissions.length === 0
                              ? t('No sections granted')
                              : t('{count} sections', { count: member.permissions.length })}
                            {member.site_scope?.length
                              ? ` · ${t('{count} sites', { count: member.site_scope.length })}`
                              : ''}
                          </span>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              <p className="cps-hint">
                <FiShield aria-hidden="true" />
                {t('Sub-account permissions are set by the client’s primary contact, but never exceed what you enable above.')}
              </p>
            </section>
          </div>
        )}

        <footer className="cps-modal__foot">
          <button type="button" className="btn btn--primary" onClick={onClose}>
            {t('Done')}
          </button>
        </footer>
      </div>
    </div>
  );
}
